/** Scoped calendar snapshots make revisits immediate without surviving expiry or permission changes. */
import type { CalendarEvent, CalendarEventColor } from '../types/calendar';

export interface CalendarFeedScope { userId: string; workspaceId: string; from: string; to: string }
export type CalendarFeedSource = 'events' | 'googleEvents' | 'googleTasks';
type Identity = Pick<CalendarFeedScope, 'userId' | 'workspaceId'>;
type Entry = { version: 1; key: string; expiresAt: number; events: CalendarEvent[] };
type Flight = { active: boolean; promise: Promise<CalendarEvent[]> };
const PREFIX = 'quantis-calendar-feed:';
const TTL = 5 * 60_000;
const LIMIT = 24;
const colors: CalendarEventColor[] = ['purple', 'blue', 'green', 'yellow', 'red', 'pink', 'indigo'];
const entries = new Map<string, Entry>();
const flights = new Map<string, Flight>();

export class CalendarFeedInvalidatedError extends Error {
  constructor() { super('Calendar feed invalidated'); this.name = 'CalendarFeedInvalidatedError'; }
}

function keyFor(scope: CalendarFeedScope, source: CalendarFeedSource): string {
  return `${PREFIX}${JSON.stringify([scope.userId, scope.workspaceId, scope.from, scope.to, source])}`;
}
function validScope(scope: CalendarFeedScope): boolean {
  return Boolean(scope.userId && scope.workspaceId)
    && Number.isFinite(Date.parse(scope.from)) && Number.isFinite(Date.parse(scope.to))
    && Date.parse(scope.to) > Date.parse(scope.from);
}
function isEvent(value: unknown): value is CalendarEvent {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  if (!['id', 'user_id', 'title', 'start_time', 'end_time', 'created_at', 'updated_at']
    .every(field => typeof row[field] === 'string')
    || !Number.isFinite(Date.parse(row.start_time as string))
    || !Number.isFinite(Date.parse(row.end_time as string))
    || typeof row.all_day !== 'boolean' || typeof row.is_google_event !== 'boolean'
    || !colors.includes(row.color as CalendarEventColor)) return false;
  for (const field of ['description', 'location', 'task_id', 'google_event_id', 'google_task_id', 'google_task_list_id', 'rrule', 'master_event_id', 'original_start_time']) {
    if (row[field] != null && typeof row[field] !== 'string') return false;
  }
  for (const field of ['is_flexible_task', 'is_google_task', 'blocks_bookings']) {
    if (row[field] != null && typeof row[field] !== 'boolean') return false;
  }
  return row.exdate == null || (Array.isArray(row.exdate) && row.exdate.every(date => typeof date === 'string'));
}
function remove(key: string): void {
  entries.delete(key);
  try { sessionStorage.removeItem(key); } catch { /* Browser storage is optional. */ }
}
function storageKeys(): string[] {
  try { return Object.keys(sessionStorage).filter(key => key.startsWith(PREFIX)); }
  catch { return []; }
}
function remember(key: string, entry: Entry): void {
  entries.delete(key);
  entries.set(key, entry);
  while (entries.size > LIMIT) remove(entries.keys().next().value!);
}

export function readCalendarFeed(scope: CalendarFeedScope, source: CalendarFeedSource): CalendarEvent[] | undefined {
  if (!validScope(scope)) return undefined;
  const key = keyFor(scope, source);
  let entry: unknown = entries.get(key);
  if (!entry) {
    try { entry = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { /* Browser storage is optional. */ }
  }
  const candidate = entry as Partial<Entry> | null;
  const now = Date.now();
  if (!candidate || candidate.version !== 1 || candidate.key !== key
    || typeof candidate.expiresAt !== 'number' || !Number.isFinite(candidate.expiresAt)
    || candidate.expiresAt <= now || candidate.expiresAt > now + TTL
    || !Array.isArray(candidate.events) || !candidate.events.every(isEvent)
    || (source === 'events' && candidate.events.some(event => event.user_id !== scope.userId))) {
    remove(key);
    return undefined;
  }
  remember(key, candidate as Entry);
  return candidate.events;
}

export function loadCalendarFeed(
  scope: CalendarFeedScope,
  source: CalendarFeedSource,
  fetcher: () => Promise<CalendarEvent[]>,
): Promise<CalendarEvent[]> {
  if (!validScope(scope)) return Promise.reject(new TypeError('Invalid calendar feed scope'));
  const cached = readCalendarFeed(scope, source);
  if (cached) return Promise.resolve(cached);
  const key = keyFor(scope, source);
  const pending = flights.get(key);
  if (pending) return pending.promise;
  const flight: Flight = { active: true, promise: Promise.resolve([]) };
  flight.promise = Promise.resolve().then(fetcher).then(events => {
    // An old request must not restore records after logout or a realtime invalidation.
    if (!flight.active) throw new CalendarFeedInvalidatedError();
    if (!Array.isArray(events) || !events.every(isEvent)
      || (source === 'events' && events.some(event => event.user_id !== scope.userId))) {
      throw new TypeError('Invalid calendar feed response');
    }
    const entry: Entry = { version: 1, key, events, expiresAt: Date.now() + TTL };
    remember(key, entry);
    try {
      sessionStorage.setItem(key, JSON.stringify(entry));
      const keys = storageKeys();
      for (const old of keys.slice(0, Math.max(0, keys.length - LIMIT))) remove(old);
    } catch { /* Quota and disabled storage must not prevent a successful calendar load. */ }
    return events;
  }).finally(() => { if (flights.get(key) === flight) flights.delete(key); });
  flights.set(key, flight);
  return flight.promise;
}

export function invalidateCalendarFeeds(scope: Identity, sources?: readonly CalendarFeedSource[]): void {
  const keys = new Set([...entries.keys(), ...flights.keys(), ...storageKeys()]);
  for (const key of keys) {
    let parts: unknown;
    try { parts = JSON.parse(key.slice(PREFIX.length)); } catch { remove(key); continue; }
    if (!Array.isArray(parts) || parts[0] !== scope.userId || parts[1] !== scope.workspaceId
      || (sources && !sources.includes(parts[4] as CalendarFeedSource))) continue;
    const flight = flights.get(key);
    if (flight) flight.active = false;
    flights.delete(key);
    remove(key);
  }
}

export function clearCalendarFeedCache(): void {
  for (const flight of flights.values()) flight.active = false;
  flights.clear();
  entries.clear();
  for (const key of storageKeys()) remove(key);
}

if (typeof window !== 'undefined') {
  window.addEventListener('quantis:auth-cleared', clearCalendarFeedCache);
  // Reuse the navigation permission bridge without importing its Supabase-backed service.
  window.addEventListener('workspace-navigation:changed', clearCalendarFeedCache);
}
